import { fileToBase64 } from "./fileToBase64";

export const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const IMAGE_UPLOAD_PRESETS = {
  avatar: {
    maxSizeMb: 5,
    maxOutputKb: 220,
    maxWidth: 720,
    maxHeight: 720,
    quality: 0.74,
    minQuality: 0.52,
  },
  catalog: {
    maxSizeMb: 6,
    maxOutputKb: 420,
    maxWidth: 1280,
    maxHeight: 1280,
    quality: 0.74,
    minQuality: 0.5,
  },
  brand: {
    maxSizeMb: 4,
    maxOutputKb: 300,
    maxWidth: 1000,
    maxHeight: 1000,
    quality: 0.8,
    minQuality: 0.58,
  },
  banner: {
    maxSizeMb: 8,
    maxOutputKb: 650,
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.76,
    minQuality: 0.52,
  },
  qris: {
    maxSizeMb: 4,
    maxOutputKb: 700,
    maxWidth: 1600,
    maxHeight: 1600,
    quality: 0.9,
    minQuality: 0.76,
  },
};

export const resolveImageUploadOptions = ({ preset = "catalog", ...overrides } = {}) => ({
  ...(IMAGE_UPLOAD_PRESETS[preset] || IMAGE_UPLOAD_PRESETS.catalog),
  ...overrides,
  preset,
});

export const validateImageFile = (file, options = {}) => {
  const { label = "Gambar", maxSizeMb } = resolveImageUploadOptions(options);
  if (!(file instanceof File)) throw new Error(`${label} belum dipilih.`);
  if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error(`${label} harus berformat JPG, PNG, atau WEBP.`);
  }
  if (file.size > maxSizeMb * 1024 * 1024) {
    throw new Error(`Ukuran ${label.toLowerCase()} maksimal ${maxSizeMb} MB.`);
  }

  return file;
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Gambar gagal dikompresi."))),
      type,
      quality
    );
  });

const loadBrowserImage = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => resolve({ image, objectUrl });
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("File gambar rusak atau tidak dapat dibaca."));
    };
    image.src = objectUrl;
  });

const webpFileName = (name = "gambar") => `${name.replace(/\.[^.]+$/, "") || "gambar"}.webp`;

export const compressImageFile = async (file, options = {}) => {
  const settings = resolveImageUploadOptions(options);
  validateImageFile(file, settings);

  // SSR dan unit test tanpa Canvas tetap mendapat validasi ukuran/type.
  if (typeof document === "undefined" || typeof Image === "undefined") return file;

  const { image, objectUrl } = await loadBrowserImage(file);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    URL.revokeObjectURL(objectUrl);
    return file;
  }

  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const initialRatio = Math.min(1, settings.maxWidth / sourceWidth, settings.maxHeight / sourceHeight);
  let width = Math.max(1, Math.round(sourceWidth * initialRatio));
  let height = Math.max(1, Math.round(sourceHeight * initialRatio));
  let result = null;

  try {
    for (let resizeAttempt = 0; resizeAttempt < 6; resizeAttempt += 1) {
      canvas.width = width;
      canvas.height = height;
      context.clearRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      for (let quality = settings.quality; quality >= settings.minQuality; quality -= 0.06) {
        result = await canvasToBlob(canvas, "image/webp", Math.max(settings.minQuality, quality));
        if (result.size <= settings.maxOutputKb * 1024) break;
      }

      if (result?.size <= settings.maxOutputKb * 1024) break;
      const nextWidth = Math.max(320, Math.round(width * 0.84));
      const nextHeight = Math.max(320, Math.round(height * 0.84));
      if (nextWidth >= width && nextHeight >= height) break;
      width = nextWidth;
      height = nextHeight;
    }
  } finally {
    URL.revokeObjectURL(objectUrl);
    canvas.width = 0;
    canvas.height = 0;
  }

  if (!result || result.size > settings.maxOutputKb * 1024) {
    throw new Error(
      `${settings.label || "Gambar"} terlalu kompleks untuk dikompresi. Gunakan gambar yang lebih sederhana.`
    );
  }

  return new File([result], webpFileName(file.name), {
    type: "image/webp",
    lastModified: Date.now(),
  });
};

export const prepareImageFile = async (file, options = {}) => compressImageFile(file, options);

export const readImageFile = async (file, options = {}) => {
  const compressedFile = await prepareImageFile(file, options);
  return fileToBase64(compressedFile);
};
